import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'

import {
  clearStoredDokimoAuth,
  DokimoMemberInfo,
  getStoredDokimoAuth,
  readFileAsBase64,
  setStoredDokimoAuth,
} from '~/utils/helper'
import {getReqErrorCode, req, ReqError} from '~/utils/request'

import {fetchChargeList, getRechargeOrderStatus} from './recharge'

type ApiRejectPayload = {
  code?: number
  message: string
}

type UploadArchiveParams = {
  file: File
  onProgress?: (percent: number) => void
}

type AccountState = {
  token: string
  memberInfo?: DokimoMemberInfo
}

const getLoginMemberInfo = (data: any): DokimoMemberInfo =>
  data?.member_info || {}

const extractMemberInfoPatch = (
  data: any
): DokimoMemberInfo | null | undefined => {
  if (data?.member_info) {
    return data.member_info
  }
  if (data?.member) {
    return data.member
  }
  if (data?.token_count != null) {
    return {token_count: data.token_count}
  }
  return undefined
}

const mergeStoredMemberInfo = (
  state: AccountState,
  patch?: DokimoMemberInfo | null
) => {
  if (!patch || !state.token) {
    return state
  }

  const memberInfo = {
    ...(state.memberInfo ?? {}),
    ...patch,
  }

  setStoredDokimoAuth({
    token: state.token,
    memberInfo,
  })

  return {
    ...state,
    memberInfo,
  }
}

const initialState: AccountState = {
  token: '',
  memberInfo: undefined,
}

export const sendEmailOtp = createAsyncThunk<any, any>(
  'email_otp_send',
  async (params) => {
    return req({
      url: '/api/account/email_otp_send',
      params,
    })
  }
)

export const emailOtpLogin = createAsyncThunk<any, any>(
  'email_otp_login',
  async (params) => {
    return req({
      url: '/api/account/email_otp_login',
      params,
    })
  }
)

export const openLogin = createAsyncThunk<any, any>(
  'open_login',
  async (params) => {
    return req({
      url: '/api/account/open_login',
      params,
    })
  }
)

export const refreshMemberInfo = createAsyncThunk<
  DokimoMemberInfo | null,
  void,
  {rejectValue: ApiRejectPayload}
>('refresh_member_info', async (_, {rejectWithValue}) => {
  try {
    const data = await req({
      url: '/api/account/get_member_info',
      params: {},
    })
    return extractMemberInfoPatch(data) ?? null
  } catch (error) {
    const reqError = error as ReqError
    return rejectWithValue({
      code: getReqErrorCode(reqError),
      message: reqError.message || 'request failed',
    })
  }
})

export const restoreAccount = createAsyncThunk<any, any>(
  'restore_account',
  async () => {
    const auth = getStoredDokimoAuth()

    if (!auth?.token) {
      clearStoredDokimoAuth()
      return null
    }

    // 需完整恢复 token / memberInfo，上传时从 memberInfo.id 取 h_m
    return auth
  }
)

export const migrationImportPrecheck = createAsyncThunk<
  any,
  UploadArchiveParams,
  {rejectValue: ApiRejectPayload}
>(
  'migration_import_precheck',
  async ({file, onProgress}, {rejectWithValue}) => {
    try {
      const packagePayload = await readFileAsBase64(file)

      return await req({
        url: '/api/account/migration_import_precheck',
        params: {
          package_payload: packagePayload,
        },
        onUploadProgress: (event) => {
          if (!event.total) {
            return
          }

          onProgress?.(Math.round((event.loaded / event.total) * 100))
        },
      })
    } catch (error) {
      const reqError = error as ReqError
      return rejectWithValue({
        code: getReqErrorCode(reqError),
        message: reqError.message || 'request failed',
      })
    }
  }
)

export const deleteAccount = createAsyncThunk<
  any,
  void,
  {rejectValue: ApiRejectPayload}
>('delete_account', async (_, {rejectWithValue}) => {
  try {
    return await req({
      url: '/api/account/delete_account',
      params: {},
    })
  } catch (error) {
    const reqError = error as ReqError
    return rejectWithValue({
      code: getReqErrorCode(reqError),
      message: reqError.message || 'request failed',
    })
  }
})

export const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    logout: (state) => {
      clearStoredDokimoAuth()
      return {
        ...state,
        token: '',
        memberInfo: undefined,
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(restoreAccount.fulfilled, (state, action) => {
        if (!action.payload) {
          return {
            ...state,
            token: '',
            memberInfo: undefined,
          }
        }

        return {
          ...state,
          ...action.payload,
        }
      })
      .addCase(restoreAccount.rejected, (state) => {
        return {
          ...state,
          token: '',
          memberInfo: undefined,
        }
      })
      .addCase(refreshMemberInfo.fulfilled, (state, action) =>
        mergeStoredMemberInfo(state, action.payload)
      )
      .addCase(fetchChargeList.fulfilled, (state, action) =>
        mergeStoredMemberInfo(state, action.payload.memberInfo)
      )
      .addCase(getRechargeOrderStatus.fulfilled, (state, action) => {
        if (Number(action.payload.status) !== 1) {
          return state
        }

        const patch =
          action.payload.member_info ??
          (action.payload.token_count != null
            ? {token_count: action.payload.token_count}
            : undefined)

        return mergeStoredMemberInfo(state, patch)
      })
      .addCase(emailOtpLogin.fulfilled, (state, action) => {
        const data = action.payload || {}
        const memberInfo = getLoginMemberInfo(data)
        const nextAuth = {
          token: data.token || '',
          memberInfo,
        }

        setStoredDokimoAuth(nextAuth)

        return {
          ...state,
          ...nextAuth,
        }
      })
      .addCase(openLogin.fulfilled, (state, action) => {
        const data = action.payload || {}
        const memberInfo = getLoginMemberInfo(data)
        const nextAuth = {
          token: data.token || '',
          memberInfo,
        }

        setStoredDokimoAuth(nextAuth)

        return {
          ...state,
          ...nextAuth,
        }
      })
      .addCase(deleteAccount.fulfilled, (state) => {
        clearStoredDokimoAuth()
        return {
          ...state,
          token: '',
          memberInfo: undefined,
        }
      })
  },
})

export const {logout} = accountSlice.actions

export default accountSlice.reducer
